<template>   
    <div class="absolute left-0 top-32 w-full z-20 flex justify-center">
        <UiNarrowContent v-if="cartline">
            
            <div ref="innerpopupRef" class="bg-white innerpopup pb-8">
                <header class="p-4 lg:p-10 flex justify-between items-center bg-gravaalightwarmgray">
                    <h3 class="text-gravaadarkbrown text-base lg:text-4xl m-0">Great choice, item added to cart.</h3>  
                    <button @click="closePopup">
                        <UiClose />
                    </button>
                </header>
                
                <div class="grid grid-cols-1 lg:grid-cols-2">
                    
                    <div :class="['p-4 lg:p-10 grid gap-5', hasOptions ? 'grid-cols-2' : 'grid-cols-1']">
                        <div>                        
                            <ProductPdpImage class="bg-gravaalightwarmgray mb-2" :src="cartline.merchandise.product.featuredImage.url" :alt="cartline.merchandise.product.title" />
                            <p v-if="hasOptions" class="small">{{cartline.merchandise.product.title}}</p>
                        </div>
                        
                        <div v-if="hasOptions" v-for="option in cartline.merchandise.selectedOptions">                            
                            <ProductPdpImage class="mb-2" v-if="getOptionImage(option)" :src="getOptionImage(option)" :alt="cartline.merchandise.product.title" />
                            <p class="small">{{option.name}}: {{option.value}}</p>
                        </div>

                        <div v-if="cartline.merchandise.product.tags.length" v-for="tag in cartline.merchandise.product.tags">
                            
                            <ProductPdpImage class="mb-2" v-if="getTagImage(tag)" :src="getTagImage(tag)" :alt="tag" />
                            <p class="small">{{tag}}</p>    
                        </div>
                    </div>
                
                    <div class="px-4 lg:p-10 flex flex-col">
                        
                        
                        <h2 class="text-2xl">{{cartline.merchandise.product.title}}</h2>
                            
                        <UiPrice :amount="cartline.merchandise.product.priceRange.minVariantPrice.amount" />
                        <p class="small">{{cartline.merchandise.product.seo.description}}</p>
                        <p v-if="cartline.merchandise.product.customHubType" class="small">Hub type: {{cartline.merchandise.product.customHubType.value}}</p>
                        <template v-if="cartline.merchandise.product.tags.length > 0">
                                <p class="small">
                                    Included with this {{ cartline.merchandise.product.productType }}:<br/>
                                    <span v-for="(tag, index) in cartline.merchandise.product.tags" :key="index">– {{ tag }}<br/></span>
                                </p>
                        </template>
                        
                            
                    
                        <div class="mb-6">
                        <NuxtLink to="/shop/all" class="small text-gravaadarkbrown underline">
                            Continue shopping
                        </NuxtLink>                                
                        </div>
                                                    
                        <div v-if="cartStore"  @click="trackBeginCheckout">
                            <UiRippleButton class="brown w-full mb-5">Checkout now</UiRippleButton>
                        </div>
                        <NuxtLink to="/cart">
                            <UiRippleButton class="w-full ">View Cart</UiRippleButton>
                        </NuxtLink>
                    
                    </div><!-- end column -->
                </div> <!-- grid -->                              
            </div><!-- innerpopup -->
        </UiNarrowContent>
    </div>
    
</template>

<script setup>
import { useCartStore } from "~/stores/cartStore"; 
import { useBlogStore } from '@/stores/blogStore';
import { onClickOutside, useEventListener } from '@vueuse/core';
import { ref } from 'vue';

const { proxy } = useScriptGoogleTagManager({ id: 'GTM-TXPLJB8M' });


const UiRippleButton = defineAsyncComponent(() => import('@/components/ui/RippleButton.vue'));
const UiClose = defineAsyncComponent(() => import('@/components/ui/close.vue'));


const cartStore = useCartStore();
const blogStore = useBlogStore();

const innerpopupRef = ref(null);

const checkoutUrl = computed(() => {
  if (!cartStore?.cart?.checkoutUrl) return ''

  const baseUrl = new URL(cartStore.cart.checkoutUrl)

  try{

    const gaCookie = document.cookie
        .split('; ')
        .find(row => row.startsWith('_ga='))

    if (gaCookie) {
        const parts = gaCookie.split('.')
        if (parts.length >= 4) {
        const clientId = `${parts[2]}.${parts[3]}`
        baseUrl.searchParams.set('client_id', clientId)
        }
    }

    return baseUrl.toString()
    } catch (error) {
        console.error("Error generating checkout URL:", error);
        return cartStore.cart.checkoutUrl; // Fallback to original URL if error occurs
    }
})

const { data: optionsBlog, error } = await useAsyncData('optionsBlog', async () => {  
  return await blogStore.fetchBlogByHandle('product-options');
});

function trackBeginCheckout() {
  console.log('[GA4] Tracking begin_checkout event')
  
  const cart = cartStore.cart
  const lines = cart?.lines?.edges || []
  if (!lines.length) return

  const currency = cart.cost?.totalAmount?.currencyCode || 'EUR'
  const value = parseFloat(cart.cost?.totalAmount?.amount || '0')

  const items = lines.map(({ node }) => {
    const variant = node.merchandise
    const product = variant.product
    const quantity = node.quantity || 1;
    const totalAmount = parseFloat(node.cost?.totalAmount?.amount || '0');

    // Extract numeric ID from GID format if present
    let itemId = variant.sku || variant.id;
    if (itemId && typeof itemId === 'string' && itemId.includes('gid://shopify/')) {
      itemId = itemId.split('/').pop() || itemId;
    }

    return {
      item_id: itemId,
      item_name: product.title,
      item_brand: product.vendor || 'Gravaa',
      item_category: product.productType,
      item_variant: variant.title,
      price: quantity > 0 ? totalAmount / quantity : totalAmount,
      quantity: quantity
    }
  })

  try {
    console.log('Pushing begin_checkout to dataLayer', {
      currency,
      value,
      items
    });

    proxy.dataLayer.push({
      event: 'begin_checkout',
      ecommerce: {
        currency,
        value,
        items
      },
      pageUrl: window.location.href
    });
  } catch (err) {
    console.warn('Error while pushing begin_checkout analytics', err);
  }

  window.location.href = checkoutUrl.value
}

const emit = defineEmits(['close']);


const props = defineProps({
    cartline: {
        type: Object,
        required: true,
        default: () => ({
            name: '',
            description: '',
            price: 0
        })
    }
});

onClickOutside(innerpopupRef, closePopup);

function closePopup() {    
    emit('close');
}

function stringToSlug(str) {
    return str
        .toLowerCase()
        .trim()
        .replace(/[\s\W-]+/g, '-')
        .replace(/^-+|-+$/g, '');
}

function getOptionImage(option) {
  try {
    
    const slug = stringToSlug(option.name + ':' + option.value);
    
    const article = optionsBlog.value.articles.find(article => article.handle === slug);
    if (article) {
      return article.image;
    } else {
      return false;
    }
  } catch (e) {
    console.log('error', e);
    return false;
  }
}

function getTagImage(tag) {
  try {
    
    
    const article = optionsBlog.value.articles.find(article => article.title === tag);
    if (article) {
      return article.image;
    } else {
      return false;
    }
  } catch (e) {
    console.log('error', e);
    return false;
  }
}

    const hasOptions = computed(() => {
        
        try{
            return props.cartline.merchandise.selectedOptions[0].name !== 'Title';    
        }    
        catch(e){
            console.log('error', e);
            return false;
        }   
    });

    onMounted(() => {
    useEventListener('keydown', (event) => {
        if (event.key === 'Escape') {
            closePopup();
        }
    })
   })

   
</script>

<style scoped>
@reference "../../assets/css/main.css";


.innerpopup{
    @apply shadow-[0_0_75px_#563B2F];
}
</style>